import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Activity, Gift, StickyNote, Sun } from "lucide-react";
import { api } from "@/lib/api";

const KIND = {
  read: { label: "Advisor read", icon: Sparkles, tone: "bg-mauve/15 text-mauve" },
  event: { label: "Pattern event", icon: Activity, tone: "bg-blush/30 text-cocoa" },
  gift: { label: "Gift / support", icon: Gift, tone: "bg-gold/20 text-cocoa" },
  note: { label: "Your note", icon: StickyNote, tone: "bg-champagne/60 text-cocoa" },
  checkin: { label: "Check-in", icon: Sun, tone: "bg-ivory text-cocoa/70" },
};
const FILTERS = [["all", "Everything"], ["read", "Reads"], ["event", "Events"], ["gift", "Gifts & promises"], ["note", "Notes"], ["checkin", "Check-ins"]];

const fmt = (s) => {
  if (!s) return "";
  const d = new Date(s.length === 10 ? `${s}T12:00:00Z` : s);
  return isNaN(d) ? s : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

export function PersonaTimeline({ profileId }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!profileId) return;
    api.get(`/profiles/${profileId}/timeline-view`).then((r) => setData(r.data)).catch(() => setErr("Couldn't load the timeline."));
  }, [profileId]);

  if (err) return <p data-testid="persona-timeline-error" className="mt-6 text-sm text-rose">{err}</p>;
  if (!data) return <p data-testid="persona-timeline-loading" className="mt-6 text-sm text-cocoa/40">Laying out the timeline…</p>;

  const items = filter === "all" ? data.items : data.items.filter((i) => i.kind === filter);
  return (
    <section data-testid="persona-timeline" className="mt-8 ss-card p-6 sm:p-7">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Timeline</p>
          <h2 className="mt-1 font-heading text-2xl text-cocoa">{data.profile.alias}, over time</h2>
          <p className="mt-1 text-xs text-cocoa/55">Reads, detected events, gifts and promises, your notes and morning check-ins — only about {data.profile.alias}.</p>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="persona-timeline-filters">
          {FILTERS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`persona-timeline-filter-${k}`}
              className={`h-8 px-3 rounded-full text-xs border transition-colors ${filter === k ? "bg-cocoa text-ivory border-cocoa" : "border-champagne text-cocoa/70 hover:border-mauve"}`}>
              {l}{k !== "all" && data.counts[k] ? ` · ${data.counts[k]}` : ""}
            </button>
          ))}
        </div>
      </div>

      {items.length === 0 ? (
        <p data-testid="persona-timeline-empty" className="mt-6 text-sm text-cocoa/55">Nothing here yet. <Link to={`/ask?profile_id=${profileId}`} className="text-mauve underline">Ask about {data.profile.alias}</Link> and the timeline starts filling in.</p>
      ) : (
        <ol data-testid="persona-timeline-list" className="mt-6 relative border-l border-champagne/70 ml-2 space-y-5 max-h-[32rem] overflow-y-auto pr-2">
          {items.map((it, idx) => {
            const K = KIND[it.kind] || KIND.note;
            return (
              <li key={`${it.kind}-${idx}`} data-testid={`persona-timeline-item-${it.kind}`} className="relative pl-6">
                <span className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 border-ivory flex items-center justify-center ${K.tone}`}><K.icon size={9} /></span>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                  <span className="text-[10px] tracking-[0.3em] uppercase text-cocoa/45">{K.label}</span>
                  <span className="text-xs text-cocoa/50">{fmt(it.at)}</span>
                  {it.state && <span className={`text-[10px] px-2 py-0.5 rounded-full ${it.state === "delivered" ? "bg-gold/25" : "bg-blush/30"}`}>{it.state}</span>}
                  {it.recurrence > 1 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-mauve/15 text-mauve">recurring ×{it.recurrence}</span>}
                </div>
                <p className="mt-0.5 text-sm text-cocoa font-medium">{it.title}</p>
                {it.detail && <p className="mt-0.5 text-sm text-cocoa/70 leading-snug">{it.detail}</p>}
                {it.ref?.session_id && it.kind === "read" && (
                  <Link to={`/history/advisor/${it.ref.session_id}`} className="mt-1 inline-block text-xs text-mauve underline">Reopen this read</Link>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
